import { filter_data, get_overall_average } from "reports/Tag/calculations";
import { calculate_engagement_by_anchor } from "./caluclate_engagement";

// Utility function to check if a value is a valid number (not 'N/A' or other non-numeric values)
// This function filters out various forms of 'N/A' values that might appear in survey responses
// and ensures only valid numeric responses are used in calculations
function isValidNumericResponse(value) {
    return value !== null && 
           value !== undefined && 
           value !== 'N/A' && 
           value !== 'n/a' && 
           value !== 'NA' && 
           value !== 'na' && 
           typeof value === 'number' && 
           !isNaN(value);
}

// Utility function to parse JSON responses from GPT
function parseGPTJsonResponse(gptResponse) {
    try {
        // Remove markdown code block delimiters if they exist
        let cleanedResponse = gptResponse.trim();
        
        // Remove ```json and ``` markers
        if (cleanedResponse.startsWith('```json')) {
            cleanedResponse = cleanedResponse.replace(/^```json\s*/i, '');
        }
        if (cleanedResponse.startsWith('```')) {
            cleanedResponse = cleanedResponse.replace(/^```\s*/, '');
        }
        if (cleanedResponse.endsWith('```')) {
            cleanedResponse = cleanedResponse.replace(/\s*```$/, '');
        }
        
        // Parse the JSON
        const parsedJson = JSON.parse(cleanedResponse);
        return parsedJson;
    } catch (error) {
        console.error("Error parsing GPT JSON response:", error);
        console.error("Original response:", gptResponse);
        
        // Return a fallback structure if parsing fails
        return {
            overview: "Error parsing response",
            quotes: [],
            insights: "Unable to parse insights from response",
            conclusion: "Response parsing failed"
        };
    }
}

// LLM API function - supports multiple models including OpenAI and Grok
async function callLLM(userPrompt, onTokenUpdate = null, model = "gpt-4.1") {
    try {
        let apiUrl, headers, requestBody;
        
        // Configure API based on selected model
        if (model === "grok-4-latest") {
            // Grok API configuration
            apiUrl = "https://api.x.ai/v1/chat/completions";
            headers = {
                "Content-Type": "application/json",
                Authorization: `Bearer xai-LaVvLzCLdUTPt2ns9qNaPMz0BWJK8mwoHGlQlvZvUpeFbgZKyppsISdSnMIJbYDCKxdIAUeIT27SKlt9`,
            };
            requestBody = {
                model: "grok-4-latest",
                messages: [
                    { role: "user", content: userPrompt },
                ],
                stream: false,
                temperature: 0.7
            };
        } else {
            // OpenAI API configuration (default)
            apiUrl = "https://api.openai.com/v1/chat/completions";
            headers = {
                "Content-Type": "application/json",
                Authorization: `Bearer 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`,
            };
            requestBody = {
                model: model,
                seed: 1,
                messages: [
                    { role: "user", content: userPrompt },
                ],
                stream: false,
            };
        }

        const response = await fetch(apiUrl, {
            method: "POST",
            headers: headers,
            body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
            throw new Error(`LLM API responded with status ${response.status}`);
        }

        const data = await response.json();
        const assistantMessage = data.choices?.[0]?.message?.content || "";
        const usage = data.usage || {};
        
        const tokenInfo = {
            prompt_tokens: usage.prompt_tokens || 0,
            completion_tokens: usage.completion_tokens || 0,
            total_tokens: usage.total_tokens || 0
        };

        // Call the token update callback if provided
        if (onTokenUpdate) {
            onTokenUpdate(tokenInfo);
        }

        return {
            content: assistantMessage,
            tokens: tokenInfo
        };
    } catch (error) {
        console.error("Error calling LLM:", error);
        throw error;
    }
}

export async function calculateSummary(raw_data, core_data, debriefSchedules, anchorParentID, onTokenUpdate = null, selectedModel = "gpt-4.1") {
    const questions = core_data.questions;
    const outcomeQuestions = core_data.outcomeQuestions;

    // Initialize token tracking
    let totalTokenUsage = {
        prompt_tokens: 0,
        completion_tokens: 0,
        total_tokens: 0,
        call_count: 0
    };

    const updateTokens = (newTokens) => {
        totalTokenUsage.prompt_tokens += newTokens.prompt_tokens;
        totalTokenUsage.completion_tokens += newTokens.completion_tokens;
        totalTokenUsage.total_tokens += newTokens.total_tokens;
        totalTokenUsage.call_count += 1;
        
        // Notify the UI of token updates
        if (onTokenUpdate) {
            onTokenUpdate(totalTokenUsage);
        }
    };

    // Find the selected anchor parent by ID
    const selectedAnchorParent = core_data.categories[0].categories.find(cat => cat.id === anchorParentID);
    
    if (!selectedAnchorParent) {
        throw new Error(`Anchor parent with ID ${anchorParentID} not found`);
    }

    // Calculate overall scores across all data
    const overallLatestScore = get_overall_average(raw_data[0].responses);
    const overallLastScore = get_overall_average(raw_data[0].last);
    const overallChange = overallLatestScore - overallLastScore;

    // Calculate overall factor scores across all data (not filtered by anchor)
    const overallLatestFactorScores = calculateAverageScoresPerFactor(raw_data[0].responses);
    const overallLastFactorScores = calculateAverageScoresPerFactor(raw_data[0].last);
    
    // Get formatted overall factor data with changes
    const overallFactorData = getChangeData(overallLatestFactorScores, overallLastFactorScores, questions);

    // Calculate participation rate for the selected anchor parent
    const participationRate = calculate_engagement_by_anchor(
        debriefSchedules.culture_audit_reports.response,
        anchorParentID,
        core_data.categories,
        core_data.orgCategories
    );


    // Collect individual reports for each anchor child
    const anchorReports = [];

    // Iterate through each of the anchor options in the selected anchor parent
    for (const option of selectedAnchorParent.options) {
        const anchorID = option.id;
        const anchorName = option.name;
        const filteredData = filter_data(raw_data[0].responses, [{id: anchorID}]);
        const lastFilteredData = filter_data(raw_data[0].last, [{id: anchorID}]);
                
        // Get the anchor data
        const latestFactorScores = calculateAverageScoresPerFactor(filteredData);
        const lastFactorScores = calculateAverageScoresPerFactor(lastFilteredData);
        
        // Calculate outcome scores
        const latestOutcomeScores = calculateAverageScoresPerOutcome(filteredData, outcomeQuestions);
        const lastOutcomeScores = calculateAverageScoresPerOutcome(lastFilteredData, outcomeQuestions);
        
        const overallAverage = get_overall_average(filteredData);
        const lastOverallAverage = get_overall_average(lastFilteredData);
        
        // Count total feedback received for this anchor
        const totalFeedbackCount = filteredData.length;

        // Get the specific participation data for this anchor
        const currentAnchorParticipation = participationRate.find(p => p.anchor_id === anchorID);

        const changeDataWithFeedback = getChangeData(latestFactorScores, lastFactorScores, questions, filteredData, lastFilteredData);

        // Sort factors by latest_score to identify lowest scoring factors
        const factorsByScore = [...changeDataWithFeedback].sort((a, b) => {
            const scoreA = typeof a.latest_score === 'number' ? a.latest_score : 0;
            const scoreB = typeof b.latest_score === 'number' ? b.latest_score : 0;
            return scoreA - scoreB; // Ascending order (lowest first)
        });

        // Get the 3 lowest scoring factors
        const lowest3Factors = factorsByScore
            .filter(f => typeof f.latest_score === 'number')
            .slice(0, 3);

        // Calculate top 3 biggest changes or top 3 scores if no significant changes
        const factorsByChange = [...changeDataWithFeedback]
            .filter(f => typeof f.latest_score === 'number' && typeof f.last_score === 'number')
            .sort((a, b) => Math.abs(b.score_change) - Math.abs(a.score_change)); // Sort by absolute change

        // If no significant changes (all changes < 5%), fall back to top 3 highest scores
        const hasSignificantChanges = factorsByChange.some(f => Math.abs(f.score_change) >= 0.5);
        
        let top3FactorsForAnalysis;
        if (hasSignificantChanges) {
            top3FactorsForAnalysis = factorsByChange.slice(0, 3);
        } else {
            // Fall back to top 3 highest scoring factors
            top3FactorsForAnalysis = [...changeDataWithFeedback]
                .filter(f => typeof f.latest_score === 'number')
                .sort((a, b) => b.latest_score - a.latest_score)
                .slice(0, 3);
        }

        // Generate detailed LLM analysis for each of the 3 lowest factors
        const detailedFactorAnalysis = [];
        for (const factor of lowest3Factors) {
            const factorAnalysisPrompt = `
            Please analyze the following feedback data for this underperforming culture factor:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            
            **Latest Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Please provide a JSON response with the following structure:
            {
                "what_this_means_for_you": ["Point 1 - one sentence", "Point 2 - one sentence", "Point 3 - one sentence"],
                "day_to_day_impacts": ["Impact 1 - one sentence", "Impact 2 - one sentence", "Impact 3 - one sentence"],
                "impactful_quotes": ["Quote 1", "Quote 2", "Quote 3", "Quote 4", "Quote 5", "Quote 6"]
            }
            
            **Guidelines:**
            - "what_this_means_for_you": Provide exactly 3 points, each as one clear sentence explaining what this low score specifically means in the simplest terms
            - "day_to_day_impacts": Provide exactly 3 points, each as one sentence describing concrete daily effects employees experience due to this factor's low performance
            - "impactful_quotes": Extract up to 6 of the most impactful, meaningful quotes directly from the feedback provided. For English quotes, use the exact wording. For quotes in any other language, you MUST translate the entire quote text to English first, then add "(translated)" at the end. The quote text itself must be in English. If fewer than 6 impactful quotes exist, provide what's available.
            - Be specific and actionable
            - Focus on practical implications
            - Use clear, jargon-free language
            - Base insights on the actual feedback provided
            `;
            
            const gptResult = await callLLM(factorAnalysisPrompt, updateTokens, selectedModel);
            const parsedAnalysis = parseGPTJsonResponse(gptResult.content);

            // Generate key takeaways for this specific factor
            const factorTakeawaysPrompt = `
            Based on the following data for this specific culture factor, provide 4 key takeaways:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            **Score Change:** ${factor.score_change >= 0 ? '+' : ''}${(factor.score_change * 10).toFixed(1)}%
            
            **Factor Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Provide 4 key takeaways specifically for this factor. Each takeaway should be:
            - A single, clear sentence
            - Specific to this factor's performance and feedback
            - Summarative of the key themes in the feedback
            - Based on the data and feedback provided
            
            Return only a JSON array of strings:
            ["takeaway 1", "takeaway 2", "takeaway 3", "takeaway 4"]
            `;
            
            const factorTakeawaysResult = await callLLM(factorTakeawaysPrompt, updateTokens, selectedModel);
            const factorTakeaways = parseGPTJsonResponse(factorTakeawaysResult.content);

            // Generate strategic focus for this specific factor
            const factorStrategicFocusPrompt = `
            Based on the following data for this specific culture factor, provide 3 strategic considerations for leadership:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            
            **Factor Challenges from Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Provide exactly 3 strategic considerations that represent the most important things for leaders to strategize based on all the feedback. Each should be:
            - A concise final thought about what leaders should strategize/consider
            - Based directly on the feedback patterns and themes
            - Actionable strategic guidance (not just tasks)
            - Focused on leadership decision-making and priorities
            
            Return only a JSON array of 3 strings:
            ["strategic consideration 1", "strategic consideration 2", "strategic consideration 3"]
            `;
            
            const factorStrategicFocusResult = await callLLM(factorStrategicFocusPrompt, updateTokens, selectedModel);
            const factorStrategicFocus = parseGPTJsonResponse(factorStrategicFocusResult.content);

            // Generate 1 sentence summary for this factor
            const factorSummaryPrompt = `
            Based on the following data for this culture factor, provide a single sentence summary that captures the most important insight:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            **Score Change:** ${factor.score_change >= 0 ? '+' : ''}${(factor.score_change * 10).toFixed(1)}%
            
            **Factor Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Provide one clear, concise sentence that summarizes the most critical insight about this factor's performance. The sentence should:
            - Capture the essence of what's happening with this factor
            - Don't reference the score
            - The simplest sentence that best describes the theme of the feedback for this factor
            - Be easily understood by leadership
            - A Single sentence that isn't too long
            
            Return only the single sentence summary, no additional formatting or explanation.
            `;
            
            const factorSummaryResult = await callLLM(factorSummaryPrompt, updateTokens, selectedModel);

            // Generate factor definition based on organizational context and feedback
            const factorDefinitionPrompt = `
            Based on the following culture factor and organizational feedback, provide a clear definition of what this factor means for organizations:
            
            **Factor:** ${factor.factor_name}
            
            Create a definition that explains:
            - What this factor represents in the workplace
            
            The definition should be:
            - 1 sentence long
            - Written in clear, professional language
            - Applicable to organizational leaders and HR professionals
            
            Return only the definition, no additional formatting or explanation.
            `;
            
            const factorDefinitionResult = await callLLM(factorDefinitionPrompt, updateTokens, selectedModel);
            
            detailedFactorAnalysis.push({
                factor_name: factor.factor_name,
                latest_score: factor.latest_score,
                last_score: factor.last_score,
                score_change: factor.score_change * 10, // Convert to percentage
                definition: factorDefinitionResult.content,
                summary: factorSummaryResult.content,
                analysis: {
                    what_this_means_for_you: Array.isArray(parsedAnalysis.what_this_means_for_you) ? parsedAnalysis.what_this_means_for_you : [],
                    day_to_day_impacts: Array.isArray(parsedAnalysis.day_to_day_impacts) ? parsedAnalysis.day_to_day_impacts : [],
                    impactful_quotes: Array.isArray(parsedAnalysis.impactful_quotes) ? parsedAnalysis.impactful_quotes : []
                },
                key_takeaways: Array.isArray(factorTakeaways) ? factorTakeaways : [],
                strategic_focus: Array.isArray(factorStrategicFocus) ? factorStrategicFocus : []
            });
        } 

        // Generate analysis for top 3 biggest changes or top 3 scores
        const top3FactorsAnalysis = [];
        for (const factor of top3FactorsForAnalysis) {
            // Generate 1 sentence summary for this factor
            const factorSummaryPrompt = `
            Based on the following data for this culture factor, provide a single sentence summary that captures the most important insight:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            **Score Change:** ${factor.score_change >= 0 ? '+' : ''}${(factor.score_change * 10).toFixed(1)}%
            
            **Factor Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Provide one clear, concise sentence that summarizes the most critical insight about this factor's performance. The sentence should:
            - Capture the essence of what's happening with this factor
            - Don't reference the score
            - The simplest sentence that best describes the theme of the feedback for this factor
            - Be easily understood by leadership
            - A Single sentence that isn't too long
            
            Return only the single sentence summary, no additional formatting or explanation.
            `;
            
            const factorSummaryResult = await callLLM(factorSummaryPrompt, updateTokens, selectedModel);

            // Generate point-form analysis for positives and change analysis
            const factorAnalysisPrompt = `
            Based on the following data for this culture factor, provide analysis in point form covering positives, what went well, and change analysis:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            **Score Change:** ${factor.score_change >= 0 ? '+' : ''}${(factor.score_change * 10).toFixed(1)}%
            
            **Factor Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Please provide a JSON response with the following structure:
            {
                "positives": ["point 1", "point 2", "point 3"],
                "what_went_well": ["point 1", "point 2", "point 3"],
                "change_analysis": ["point 1", "point 2", "point 3"]
            }
            
            **Guidelines:**
            - Each point should be a clear, concise statement
            - Focus on specific insights from the feedback
            - For "positives": highlight the strengths and positive aspects of this factor
            - For "what_went_well": explain specific things that are working well based on feedback
            - For "change_analysis": if there's significant change (>5%), explain root causes; if no change, explain why this factor maintains its performance
            - Base all points on the actual feedback and data provided
            - Keep each point to 1-2 sentences maximum
            `;
            
            const factorAnalysisResult = await callLLM(factorAnalysisPrompt, updateTokens, selectedModel);
            const parsedAnalysis = parseGPTJsonResponse(factorAnalysisResult.content);

            top3FactorsAnalysis.push({
                factor_name: factor.factor_name,
                latest_score: factor.latest_score,
                last_score: factor.last_score,
                score_change: factor.score_change * 10, // Convert to percentage
                summary: factorSummaryResult.content,
                positives: Array.isArray(parsedAnalysis.positives) ? parsedAnalysis.positives : [],
                what_went_well: Array.isArray(parsedAnalysis.what_went_well) ? parsedAnalysis.what_went_well : [],
                change_analysis: Array.isArray(parsedAnalysis.change_analysis) ? parsedAnalysis.change_analysis : []
            });
        }



        // Generate "4 things to start this week" for this anchor
        const thingsToStartPrompt = `
        Based on the following organizational data, provide 4 specific things this organization should start doing this week to improve their culture:
        
        **Organization:** ${anchorName}
        **Overall Score:** ${overallAverage.toFixed(2)}
        
        **Lowest Performing Areas:**
        ${lowest3Factors.map(f => `- ${f.factor_name}: ${f.latest_score.toFixed(2)}`).join('\n')}
        
        **Key Challenges from Feedback:**
        ${lowest3Factors.map(f => f.latest_feedback || "No feedback").join('\n\n')}
        
        Provide 4 actionable items they can start this week. Each should be:
        - Immediately actionable (can be started within 7 days)
        - Specific and concrete
        - Directly address the lowest performing areas
        - Realistic for a typical organization to implement
        
        Return a JSON array with this format:
        [
            {
                "title": "Short, clear title (2-6 words)",
                "description": "One sentence describing what to do and why it matters"
            },
            {
                "title": "Short, clear title (2-6 words)", 
                "description": "One sentence describing what to do and why it matters"
            },
            {
                "title": "Short, clear title (2-6 words)",
                "description": "One sentence describing what to do and why it matters"
            },
            {
                "title": "Short, clear title (2-6 words)",
                "description": "One sentence describing what to do and why it matters"
            }
        ]
        `;
        
        const thingsToStartResult = await callLLM(thingsToStartPrompt, updateTokens, selectedModel);
        const thingsToStart = parseGPTJsonResponse(thingsToStartResult.content);

        // Generate "4 habits to build" for this anchor
        const habitsToBuildPrompt = `
        Based on the following organizational data, provide 3 long-term habits this organization should build to create lasting culture improvement:
        
        **Organization:** ${anchorName}
        **Overall Score:** ${overallAverage.toFixed(2)}
        
        **Lowest Performing Areas:**
        ${lowest3Factors.map(f => `- ${f.factor_name}: ${f.latest_score.toFixed(2)}`).join('\n')}
        
        **Key Challenges from Feedback:**
        ${lowest3Factors.map(f => f.latest_feedback || "No feedback").join('\n\n')}
        
        Provide 3 habits that should become regular practices. Each should be:
        - Sustainable long-term practices
        - Address root causes of low scores
        - Be specific and measurable
        - Focus on building positive organizational behaviors
        
        Return a JSON array with this format:
        [
            {
                "title": "Short, clear title (2-6 words)",
                "description": "One sentence describing the habit and its long-term benefit"
            },
            {
                "title": "Short, clear title (2-6 words)", 
                "description": "One sentence describing the habit and its long-term benefit"
            },
            {
                "title": "Short, clear title (2-6 words)",
                "description": "One sentence describing the habit and its long-term benefit"
            }
        ]
        `;
        
        const habitsToBuildResult = await callLLM(habitsToBuildPrompt, updateTokens, selectedModel);
        const habitsToBuild = parseGPTJsonResponse(habitsToBuildResult.content);

        // Build the individual report for this anchor
        const anchorReport = {
            anchor_id: anchorID,
            anchor_name: anchorName,
            overall_score: overallAverage,
            previous_overall_score: lastOverallAverage,
            score_change: (overallAverage - lastOverallAverage) * 10, // Convert to percentage
            participation_rate: currentAnchorParticipation,
            total_feedback_count: totalFeedbackCount,
            all_factors: changeDataWithFeedback.map(factor => ({
                factor_name: factor.factor_name,
                latest_score: factor.latest_score,
                last_score: factor.last_score,
                score_change: factor.score_change * 10 // Convert to percentage
            })),
            lowest_3_factors_analysis: detailedFactorAnalysis,
            top_3_factors_analysis: top3FactorsAnalysis,
            analysis_type: hasSignificantChanges ? "biggest_changes" : "top_scores",
            overall_llm_analysis: {
                things_to_start_this_week: Array.isArray(thingsToStart) ? thingsToStart : [],
                habits_to_build: Array.isArray(habitsToBuild) ? habitsToBuild : []
            },
            outcome_scores: {
                latest: latestOutcomeScores,
                previous: lastOutcomeScores
            }
        };
        
        anchorReports.push(anchorReport);
    }

    // Calculate final costs
    const inputCost = (totalTokenUsage.prompt_tokens / 1000000) * 2;
    const outputCost = (totalTokenUsage.completion_tokens / 1000000) * 8;
    const totalCost = inputCost + outputCost;

    // Return restructured data with individual reports per anchor
    return {
        overall_summary: {
            overall_average: overallLatestScore,
            overall_change: overallChange * 10, // Convert to percentage
            previous_average: overallLastScore
        },
        overall_factor_scores: overallFactorData.map(factor => ({
            factor_name: factor.factor_name,
            latest_score: factor.latest_score,
            previous_score: factor.last_score,
            score_change: factor.score_change * 10 // Convert to percentage
        })),
        selected_anchor_parent: {
            id: selectedAnchorParent.id,
            name: selectedAnchorParent.name
        },
        anchor_reports: anchorReports,
        token_usage: {
            ...totalTokenUsage,
            input_cost: inputCost,
            output_cost: outputCost,
            total_cost: totalCost
        }
    };
}




/**
 * Calculate the average scores per culture factor.
 * Each entry in data is expected to have a "questions" array.
 * We use a composite key "factor_dimension" (e.g. "0_1") to track each factor.
 */
function calculateAverageScoresPerFactor(data) {
  console.log(data)
    const factorScores = {};
    data.forEach(entry => {
      const questions = entry.questions || [];
      questions.forEach(question => {
        const factor = question.factor;
        const dimension = question.id; // assuming "id" represents the dimension id
        const response = question.response;
        // Only process if we have valid numeric response (filter out 'N/A' values)
        if (factor != null && dimension != null && isValidNumericResponse(response)) {
          const key = `${factor}_${dimension}`;
          if (factorScores[key]) {
            factorScores[key].sum += response;
            factorScores[key].count += 1;
          } else {
            factorScores[key] = { sum: response, count: 1, factor, dimension };
          }
        }
      });
    });
    // Calculate averages for each factor key
    const averages = {};
    Object.keys(factorScores).forEach(key => {
      // Handle case where all responses were 'N/A' (count would be 0)
      if (factorScores[key].count > 0) {
        averages[key] = factorScores[key].sum / factorScores[key].count;
      } else {
        averages[key] = "No data"; // or could be null/undefined
      }
    });
    return averages;
  }

/**
 * Calculate the average scores and high score percentage for employee outcomes.
 * Each entry in data is expected to have an "employee_outcomes" object with responses.
 * We match q and s values to outcomeQuestions to get outcome names.
 */
function calculateAverageScoresPerOutcome(data, outcomeQuestions) {
    const outcomeScores = {};
    
    // Create a mapping from q,s coordinates to outcome info
    const qsToOutcomeMap = {};
    if (outcomeQuestions && outcomeQuestions.questions) {
        outcomeQuestions.questions.forEach(outcome => {
            outcome.questions.forEach(question => {
                const key = `${question.q}_${question.s}`;
                qsToOutcomeMap[key] = {
                    outcomeId: outcome.id,
                    outcomeName: outcome.name
                };
            });
        });
    }
    
    data.forEach(entry => {
        const employeeOutcomes = entry.employee_outcomes;
        if (employeeOutcomes && employeeOutcomes.responses) {
            employeeOutcomes.responses.forEach(response => {
                const key = `${response.q}_${response.s}`;
                const outcomeInfo = qsToOutcomeMap[key];
                
                // Only process if we have valid numeric response (filter out 'N/A' values)
                if (outcomeInfo && isValidNumericResponse(response.response)) {
                    const outcomeKey = outcomeInfo.outcomeId;
                    
                    if (outcomeScores[outcomeKey]) {
                        outcomeScores[outcomeKey].responses.push(response.response);
                    } else {
                        outcomeScores[outcomeKey] = {
                            outcomeName: outcomeInfo.outcomeName,
                            responses: [response.response]
                        };
                    }
                }
            });
        }
    });
    
    // Calculate averages and high score percentages for each outcome
    const results = {};
    Object.keys(outcomeScores).forEach(outcomeKey => {
        const responses = outcomeScores[outcomeKey].responses;
        
        // Handle case where no valid numeric responses were found
        if (responses.length === 0) {
            results[outcomeKey] = {
                outcome_name: outcomeScores[outcomeKey].outcomeName,
                average_score: "No data",
                high_score_percentage: 0,
                total_responses: 0
            };
            return;
        }
        
        const sum = responses.reduce((acc, val) => acc + val, 0);
        const average = sum / responses.length;
        
        // Calculate percentage of responses that are 7 or above
        const highScores = responses.filter(response => response >= 7);
        const highScorePercentage = (highScores.length / responses.length) * 100;
        
        results[outcomeKey] = {
            outcome_name: outcomeScores[outcomeKey].outcomeName,
            average_score: average,
            high_score_percentage: highScorePercentage,
            total_responses: responses.length
        };
    });
    
    return results;
}

  function filterFeedback(responses, factor, dimension) {
    if (!responses || !Array.isArray(responses)) return [];
    
    const feedbackList = [];
    responses.forEach(entry => {
      const questions = entry.feedback || [];
      questions.forEach(question => {
        if (question.factor === factor && question.id === dimension && question.feedback) {
          feedbackList.push({
            feedback: question.feedback,
            question: question.question || "",
            follow_up: question.follow_up || "",
            follow_up_question: question.follow_up_question || "",
            prompt_question: question.prompt_question || ""
          });
        }
      });
    });
    
    return feedbackList;
  }

  function getFactorFeedback(factorTuple, responses, options = {}) {
    if (!factorTuple) return "";
    const [factor, dimension] = factorTuple;
    
    // Filter the responses
    const feedbackList = filterFeedback(responses, factor, dimension);
    if (!feedbackList.length) return "";
    
    // Depending on options, choose a formatting strategy. For now, we use one format.
    return formatFeedback(feedbackList, options);
  }
  
  function formatFeedback(feedbackList, options = {}) {
    if (!feedbackList.length) return "";
    
    const { headerName } = options;
    let formatted = headerName
      ? `### Feedback from ${headerName} responses:\n\n`
      : "### Feedback from responses:\n\n";
    
    feedbackList.forEach(item => {
      // If prompt_question exists, handle it differently
      if (item.prompt_question) {
        formatted += "Follow up: " + item.prompt_question + "\n";
        formatted += "Response: " + (item.feedback || "") + "\n";
        formatted += "\n";
        return;
      }
      
      if (item.feedback) {
        formatted += "Question asked: " + (item.question || "") + "\n";
        formatted += "Response: " + item.feedback + "\n";
      }
      
      if (item.follow_up && item.follow_up_question) {
        formatted += "Follow up: " + item.follow_up_question + "\n";
        formatted += "Response: " + item.follow_up + "\n";
      }
      
      formatted += "\n";
    });
    
    formatted += "### END OF FEEDBACK \n\n";
    return formatted;
  }

  function getFactorName(questionsStructure, factorTuple) {
    if (!factorTuple) return null;
    const [factorId, dimensionId] = factorTuple;
    const dimensions = questionsStructure.dimensions || [];
    // Find the dimension whose id equals dimensionId+1 (adjust if necessary)
    const dimension = dimensions.find(dim => dim.id === dimensionId + 1);
    if (!dimension) return null;
    const factor = dimension.factors.find(fact => fact.id === factorId + 1);
    if (!factor) return null;
    return factor.title;
  }

  function getChangeData(latestFactorScores, lastFactorScores, questions, filteredData = null, lastFilteredData = null){

    // Get the union of all factor keys (composite keys: "factor_dimension")
    const allFactorKeys = new Set([
        ...Object.keys(latestFactorScores),
        ...Object.keys(lastFactorScores)
      ]);

      const factorChanges = [];

       // Process each culture factor sequentially
       for (let key of allFactorKeys) {
        const parts = key.split("_");
        const factorId = parseInt(parts[0], 10);
        const dimensionId = parseInt(parts[1], 10);
    
        const factorName = getFactorName(questions, [factorId, dimensionId]);
        const latestScore =
          latestFactorScores[key] !== undefined
            ? latestFactorScores[key]
            : "No data";
        const lastScore =
          lastFactorScores[key] !== undefined ? lastFactorScores[key] : "No data";

        // Track the numeric score change
        const scoreChange =
          typeof latestScore === "number" && typeof lastScore === "number"
            ? latestScore - lastScore
            : 0;

        const latestFeedback = filteredData ? getFactorFeedback(
            [factorId, dimensionId],
            filteredData
          ) : "";
          const lastFeedback = lastFilteredData ? getFactorFeedback(
            [factorId, dimensionId],
            lastFilteredData
          ) : "";

        factorChanges.push({
          factor_id: key,
          factor_name: factorName,
          latest_score: latestScore,
          last_score: lastScore,
          score_change: scoreChange,
          latest_feedback: latestFeedback,
          last_feedback: lastFeedback
        });
    }

    return factorChanges;
  }